import {
	RequiredEmployeeField,
	EmployeeInviteRequirements
} from '../api/companies/getEmployeeInviteRequirements';
import { InviteEmployeeMinimal } from '../api/companies/inviteEmployeesMinimal';
import { EmployeeCategory } from '../api/users/interfaces/Users';

export interface ValidationError {
	field: string;
	message: string;
}

export interface EmployeeValidationResult {
	isValid: boolean;
	errors: ValidationError[];
}

export interface EmployeesValidationResult {
	invalidCount: number;
	employeeErrors: Array<{
		index: number;
		errors: ValidationError[];
	}>;
}

export const validateEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const validateDateFormat = (date: string): boolean => {
	const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
	const match = date.match(dateRegex);
	if (!match) return false;

	const day = parseInt(match[1], 10);
	const month = parseInt(match[2], 10);
	const year = parseInt(match[3], 10);

	if (month < 1 || month > 12 || day < 1 || day > 31) return false;

	const parsedDate = new Date(year, month - 1, day);
	return (
		parsedDate.getFullYear() === year &&
		parsedDate.getMonth() === month - 1 &&
		parsedDate.getDate() === day
	);
};

export const validateMinAge = (birthdate: string, minAge: number): boolean => {
	const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
	const match = birthdate?.match(dateRegex);
	if (!match) return false;

	const day = parseInt(match[1], 10);
	const month = parseInt(match[2], 10);
	const year = parseInt(match[3], 10);

	const today = new Date();
	const birth = new Date(year, month - 1, day);
	let age = today.getFullYear() - birth.getFullYear();
	const monthDiff = today.getMonth() - birth.getMonth();
	if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
		age--;
	}

	return age >= minAge;
};

/**
 * Valida NIF español (DNI) o NIE extranjero con verificación de letra de control
 * DNI: 8 dígitos + letra de control
 * NIE: X/Y/Z + 7 dígitos + letra de control
 */
export const validateNif = (nif: string): boolean => {
	if (!nif) return false;

	const cleanNif = nif.trim().toUpperCase();
	if (!cleanNif) return false;

	// Tabla de letras de control
	const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

	// DNI: 8 dígitos + 1 letra
	const dniRegex = /^(\d{8})([A-Z])$/;
	// NIE: X/Y/Z + 7 dígitos + 1 letra
	const nieRegex = /^([XYZ])(\d{7})([A-Z])$/;

	const dniMatch = cleanNif.match(dniRegex);
	if (dniMatch) {
		const number = parseInt(dniMatch[1], 10);
		const letter = dniMatch[2];
		const expectedLetter = NIF_LETTERS[number % 23];
		return letter === expectedLetter;
	}

	const nieMatch = cleanNif.match(nieRegex);
	if (nieMatch) {
		const niePrefix = nieMatch[1];
		const nieNumber = nieMatch[2];
		const letter = nieMatch[3];

		// Convertir prefijo NIE a número: X=0, Y=1, Z=2
		const prefixValue = { X: '0', Y: '1', Z: '2' }[niePrefix] || '0';
		const fullNumber = parseInt(prefixValue + nieNumber, 10);
		const expectedLetter = NIF_LETTERS[fullNumber % 23];
		return letter === expectedLetter;
	}

	return false;
};

/**
 * Valida NIF o NIE con mensaje de error (compatible con OnboardingUser)
 */
export const validateNifOrNie = (
	type: 'NIF' | 'NIE' | 'DNI' | string,
	value: string,
	translations?: { nifError?: string; nieError?: string }
): { isValid: boolean; message: string } => {
	if (!value || !value.trim()) {
		return { isValid: false, message: '' };
	}

	const isValid = validateNif(value);

	if (!isValid) {
		const isNie = /^[XYZ]/i.test(value.trim());
		const message = isNie
			? translations?.nieError || 'NIE inválido'
			: translations?.nifError || 'NIF inválido';
		return { isValid: false, message };
	}

	return { isValid: true, message: '' };
};

export const validateEmployee = (
	employee: InviteEmployeeMinimal,
	requirements: EmployeeInviteRequirements
): EmployeeValidationResult => {
	const errors: ValidationError[] = [];

	// Base fields validation
	if (!employee.nif?.trim()) {
		errors.push({ field: 'nif', message: 'NIF requerido' });
	} else if (!validateNif(employee.nif)) {
		errors.push({ field: 'nif', message: 'NIF/NIE inválido' });
	}

	if (!employee.email || !validateEmail(employee.email)) {
		errors.push({ field: 'email', message: 'Email inválido' });
	}

	if (!employee.name?.trim()) {
		errors.push({ field: 'name', message: 'Nombre requerido' });
	}

	if (!employee.firstSurname?.trim()) {
		errors.push({ field: 'firstSurname', message: 'Primer apellido requerido' });
	}

	if (!employee.birthdate || !validateDateFormat(employee.birthdate)) {
		errors.push({ field: 'birthdate', message: 'Fecha de nacimiento inválida (DD/MM/YYYY)' });
	} else if (!validateMinAge(employee.birthdate, 18)) {
		errors.push({ field: 'birthdate', message: 'El empleado debe tener al menos 18 años' });
	}

	if (!employee.gender || !['M', 'F'].includes(employee.gender)) {
		errors.push({ field: 'gender', message: 'Género debe ser M o F' });
	}

	// Conditional fields validation based on requirements
	const { requiredFields } = requirements;

	if (requiredFields.includes(RequiredEmployeeField.GROSS_ANNUAL_SALARY)) {
		if (
			!employee.grossAnnualSalary ||
			Number.isNaN(employee.grossAnnualSalary) ||
			employee.grossAnnualSalary <= 0
		) {
			errors.push({ field: 'grossAnnualSalary', message: 'Salario bruto anual requerido' });
		}
	}

	if (requiredFields.includes(RequiredEmployeeField.EMPLOYMENT_START_DATE)) {
		if (!employee.employmentStartDate || !validateDateFormat(employee.employmentStartDate)) {
			errors.push({
				field: 'employmentStartDate',
				message: 'Fecha de entrada inválida (DD/MM/YYYY)'
			});
		}
	}

	if (requiredFields.includes(RequiredEmployeeField.CATEGORY)) {
		const validCategories = Object.values(EmployeeCategory) as string[];
		if (!employee.category || !validCategories.includes(employee.category)) {
			errors.push({
				field: 'category',
				message: `Categoría debe ser ${validCategories.join(', ')}`
			});
		}
	}

	if (requiredFields.includes(RequiredEmployeeField.COLLECTIVE_AGREEMENT_GROUP)) {
		if (!employee.collectiveAgreementGroup?.trim()) {
			errors.push({
				field: 'collectiveAgreementGroup',
				message: 'Grupo de convenio requerido'
			});
		}
	}

	return { isValid: errors.length === 0, errors };
};

export const validateEmployees = (
	employees: InviteEmployeeMinimal[],
	requirements: EmployeeInviteRequirements
): EmployeesValidationResult => {
	let invalidCount = 0;
	const employeeErrors = employees.map((employee, index) => {
		const result = validateEmployee(employee, requirements);
		if (!result.isValid) {
			invalidCount++;
		}
		return { index, errors: result.errors };
	});

	return { invalidCount, employeeErrors };
};
